import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { deleteProject } from '../../../../actions'
import { Logger, LoggerTypes } from '../../../../utils'
import { AccessControl, AccessControlNoRights, Button, buttonModes, buttonTypes } from '../../../../components'
import { userRightTypes } from '../../../../constants'

export const ProjectDialogDelete = forwardRef(({ project, openDialog, setOpenDialog }, ref) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const onClickCancel = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectDialogDelete.onClickCancel()')
    setOpenDialog(!openDialog)
  }

  const onClickDelete = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectDialogDelete.onCloseClick()', { project })

    dispatch(deleteProject(project))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'Home.onClickDelete().dispatch().then()', { project, res })
        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        } else {
          setOpenDialog(!openDialog)
        }
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'Home.onClickDelete().dispatch().catch()', { project, res })

        const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
        enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'ProjectDialogDelete.render()', { openDialog, project })

  return (
    <Dialog ref={ref} open={openDialog} onClose={onClickCancel} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{`${t('projects.dialogs.delete.title')} ${project.name}`}</DialogTitle>
      <DialogContent>
        <AccessControl
          userPermissions={[userRightTypes.PROJECT_DELETE]}
          renderNoAccess={() => <AccessControlNoRights />}
        >
          <DialogContentText>{t('projects.dialogs.delete.description')}</DialogContentText>
        </AccessControl>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          type={buttonTypes.BUTTON}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onClickCancel}
        />
        <AccessControl userPermissions={[userRightTypes.PROJECT_DELETE]} renderNoAccess={() => <></>}>
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.SUBMIT}
            text={t('common.delete')}
            icon={<DeleteOutlineIcon />}
            onClick={onClickDelete}
          />
        </AccessControl>
      </DialogActions>
    </Dialog>
  )
})

ProjectDialogDelete.displayName = 'ProjectDialogDelete'

ProjectDialogDelete.propTypes = {
  project: PropTypes.object.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
}

ProjectDialogDelete.defaultProps = {
  project: {},
  openDialog: false,
  setOpenDialog: () => {}
}
